import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

import { API_URL as API } from "@/lib/api";

const FACTOR_PMENOR = 1.2;

const FIELDS = [
  { key: "codigo", label: "Codigo", type: "text", required: true },
  { key: "descripcion", label: "Descripcion", type: "text", required: true },
  { key: "familia", label: "Familia", type: "select" },
  { key: "subfamilia", label: "SubFamilia", type: "text" },
  { key: "unidad", label: "Unidad", type: "text" },
  { key: "cantidad_inicial", label: "Cant. Inicial", type: "number" },
  { key: "ingreso", label: "Ingreso", type: "number" },
  { key: "salida", label: "Salida", type: "number" },
  { key: "stock", label: "Stock", type: "number" },
  { key: "proveedor", label: "Proveedor", type: "text" },
  { key: "cod_barra", label: "Cod. Barra", type: "text" },
];

const EMPTY = {
  codigo: "", descripcion: "", familia: "", subfamilia: "", unidad: "PCS",
  cantidad_inicial: 0, ingreso: 0, salida: 0, stock: 0,
  precio_usd: 0, proveedor: "", cod_barra: "",
};

const ProductFormModal = ({ open, onOpenChange, product, familias = [], tipoCambio = 1, onSuccess }) => {
  const isEdit = !!product;
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      if (product) {
        setForm({ ...EMPTY, ...product, precio_usd: product.precio_usd || 0 });
      } else {
        setForm({ ...EMPTY });
      }
    }
  }, [open, product]);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const tc = tipoCambio || 1;
  const previewPMayor = useMemo(() => Math.round(form.precio_usd * tc * 100) / 100, [form.precio_usd, tc]);
  const previewPMenor = useMemo(() => Math.round(previewPMayor * FACTOR_PMENOR * 100) / 100, [previewPMayor]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.codigo.trim()) { toast.error("El codigo es requerido"); return; }
    if (!form.descripcion.trim()) { toast.error("La descripcion es requerida"); return; }

    setSubmitting(true);
    try {
      const payload = { ...form, precio_usd: form.precio_usd || 0 };
      delete payload.pmayor;
      delete payload.pmenor;

      if (isEdit) {
        const { codigo, ...updates } = payload;
        await axios.put(`${API}/api/products/crud/${product.codigo}`, updates, { withCredentials: true });
        toast.success(`Producto '${product.codigo}' actualizado`);
      } else {
        await axios.post(`${API}/api/products/crud`, payload, { withCredentials: true });
        toast.success(`Producto '${payload.codigo}' creado`);
      }
      onSuccess?.();
      onOpenChange(false);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Error al guardar producto");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="product-form-modal">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Editar Producto" : "Nuevo Producto"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3 mt-2">
          <div className="grid grid-cols-2 gap-3">
            {FIELDS.map((f) => {
              const isDisabled = isEdit && f.key === "codigo";
              if (f.type === "select" && f.key === "familia") {
                return (
                  <div key={f.key} className="space-y-1">
                    <label className="text-xs font-medium text-foreground">{f.label}</label>
                    <Select value={form.familia || "__custom__"} onValueChange={(v) => set("familia", v === "__custom__" ? "" : v)}>
                      <SelectTrigger data-testid={`product-field-${f.key}`} className="h-9 text-sm">
                        <SelectValue placeholder="Seleccionar familia" />
                      </SelectTrigger>
                      <SelectContent>
                        {familias.filter(fam => fam && fam.trim()).map((fam) => <SelectItem key={fam} value={fam}>{fam}</SelectItem>)}
                        <SelectItem value="__custom__">Escribir manualmente...</SelectItem>
                      </SelectContent>
                    </Select>
                    {(form.familia === "" || !familias.includes(form.familia)) && (
                      <Input
                        className="h-8 text-sm mt-1"
                        placeholder="Nombre de familia"
                        value={form.familia}
                        onChange={(e) => set("familia", e.target.value)}
                      />
                    )}
                  </div>
                );
              }
              return (
                <div key={f.key} className={`space-y-1 ${f.key === "descripcion" ? "col-span-2" : ""}`}>
                  <label className="text-xs font-medium text-foreground">
                    {f.label}{f.required && <span className="text-rose-500 ml-0.5">*</span>}
                  </label>
                  <Input
                    data-testid={`product-field-${f.key}`}
                    type={f.type === "number" ? "number" : "text"}
                    step={f.type === "number" ? "0.01" : undefined}
                    value={form[f.key]}
                    onChange={(e) => set(f.key, f.type === "number" ? parseFloat(e.target.value) || 0 : e.target.value)}
                    disabled={isDisabled}
                    className={`h-9 text-sm ${isDisabled ? "bg-muted" : ""}`}
                    required={f.required}
                  />
                </div>
              );
            })}
          </div>

          {/* Pricing Section - USD based */}
          <div className="border border-sky-200 bg-sky-50/50 rounded-lg p-3 space-y-3">
            <p className="text-xs font-semibold text-sky-800 uppercase tracking-wide">Precios (Base USD)</p>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <label className="text-xs font-medium text-foreground">Precio USD ($)</label>
                <Input
                  data-testid="product-field-precio_usd"
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.precio_usd}
                  onChange={(e) => set("precio_usd", parseFloat(e.target.value) || 0)}
                  className="h-9 text-sm font-medium"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">PMayor (Bs)</label>
                <Input
                  data-testid="product-field-pmayor-preview"
                  type="text"
                  value={previewPMayor.toFixed(2)}
                  disabled
                  className="h-9 text-sm bg-muted text-muted-foreground font-mono"
                />
                <p className="text-[10px] text-muted-foreground">= USD x TC ({tc})</p>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">PMenor (Bs)</label>
                <Input
                  data-testid="product-field-pmenor-preview"
                  type="text"
                  value={previewPMenor.toFixed(2)}
                  disabled
                  className="h-9 text-sm bg-muted text-muted-foreground font-mono"
                />
                <p className="text-[10px] text-muted-foreground">= PMayor x {FACTOR_PMENOR}</p>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t border-border">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={submitting} data-testid="product-form-submit" className="amazon-btn-cta">
              {submitting ? "Guardando..." : (isEdit ? "Actualizar" : "Crear Producto")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ProductFormModal;
